import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { getGoverningValueCalculations, getPipeSize } from '@/utils';
import {
  InputMaybe,
  MeasureBlisterQuery,
  TemplateTypeEnum,
  useEvaluateTemplateFieldLogicRuleMutation,
  useGetAssemblyPipeSpecByIdLazyQuery,
} from '@/__generated__/graphql';
import { PointWithHeight } from '../../../createOrEdit/BlisterMeasurement/Graph';
import { BlisterInput, UnitSystemEnum, useStructureForBlisterQuery } from '@/__generated__/graphql';
import * as localState from '@/components/Analysis/state';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { MILLIMETERS_TO_METERS_FACTOR, METERS_TO_INCHES_FACTOR } from '@/utils/unitSystem';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { PolygonProps } from '@fabric-web/3d-viewer';
import { UseTemplateFieldsHookType } from './useTemplateFields';
import {
  ControlledFieldAssignment,
  SC_CATEGORY_ASSIGNMENT_KEY,
  SC_CATEGORY_KEY,
  ScCategoryAssignment,
  WALL_LOSS_CLASS_KEY,
} from '@/constants';
import { calculateConfidence, getDistanceConfidence } from '@/utils/blisterRecommendations';
import { useBlisterRecommendation } from './useBlisterRecommendation';

const defaultBlisterData: BlisterInput = {
  maxHeight: 0,
  estimatedHeight: 0,
  manualSetHeight: 0,
  radius: 0,
  fittingRadius: 0,
  borders: [],
  borders3d: [],
  centerPoint: { bearing: 0, elevation: 0 },
  radiusPoint: { bearing: 0, elevation: 0 },
  scCategory: '',
};

/**
 * Normalizes governing values by omitting fields with null values.
 * This is necessary because GraphQL BlisterInput type requires ValueAndPercentageInput
 * fields to have non-null number values, but uncertain blisters return null.
 * @param governingValues The result from getGoverningValueCalculations
 * @returns Object with only non-null fields included
 */
const normalizeGoverningValues = (governingValues: {
  wallLoss: { value: number | null; percentage: number | null };
  residualCorrosionAllowance: { value: number | null; percentage: number | null };
  residualThickness: { value: number | null; percentage: number | null };
}): Partial<
  Pick<BlisterInput, 'wallLoss' | 'residualCorrosionAllowance' | 'residualThickness'>
> => {
  return {
    ...(governingValues.wallLoss?.value !== null && governingValues.wallLoss?.percentage !== null
      ? { wallLoss: governingValues.wallLoss as { value: number; percentage: number } }
      : {}),
    ...(governingValues.residualCorrosionAllowance?.value !== null &&
    governingValues.residualCorrosionAllowance?.percentage !== null
      ? {
          residualCorrosionAllowance: governingValues.residualCorrosionAllowance as {
            value: number;
            percentage: number;
          },
        }
      : {}),
    ...(governingValues.residualThickness?.value !== null &&
    governingValues.residualThickness?.percentage !== null
      ? {
          residualThickness: governingValues.residualThickness as {
            value: number;
            percentage: number;
          },
        }
      : {}),
  };
};

export type BlisterFieldsData = {
  estimatedHeight?: string;
  manualSetHeight?: string;
  blisterHeightIndex?: InputMaybe<number>;
  scCategory?: string;
};

export const useBlisterData = (dataHook: UseTemplateFieldsHookType, type?: string) => {
  const [editPointOfInterest, setEditPointOfInterest] = useAtom(poiState.editPointOfInterest);
  const pointOfInterest = editPointOfInterest?.pointOfInterest;
  const template = editPointOfInterest?.template;
  const [localBlisterData, setLocalBlisterData] = useState<BlisterInput | undefined>(undefined);
  const [blisterFieldsData, setBlisterFieldsData] = useState<BlisterFieldsData | undefined>(
    undefined
  );
  const [pointSelectMessage, setPointSelectMessage] = useState<boolean>(false);
  const selectedAssemblyId = useAtomValue(localState.selectedAssemblyId);
  const unitSystem = useAtomValue(localState.unitSystem);
  const setBlisterPolys = useSetAtom(poiState.blisterPolygons);
  const setIsEvaluatingFields = useSetAtom(poiState.isEvaluatingFields);
  const structureId = useGetStructureId();
  const selectedSpherical = useAtomValue(localState.viewerCurrentSpherical);
  const structurePipesByStructureId = useAtomValue(localState.structurePipesByStructureId);
  const { getDistanceFromPoint } = useBlisterRecommendation();
  const { data } = useStructureForBlisterQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  const [evaluateFieldLogic, { loading: isEvaluatingFields }] =
    useEvaluateTemplateFieldLogicRuleMutation();

  const wallLossFactor = data?.structure?.wallLossFactor;
  const scCategoryMapping = data?.structure?.scCategoryMapping;

  const scCategoryField = useMemo(() => {
    return template?.fields.find((field) => field.key === SC_CATEGORY_KEY);
  }, [template?.fields]);

  const scCategoryOptionsData = useMemo(() => {
    return template?.fields.find((field) => field.key === SC_CATEGORY_KEY)?.optionsData ?? [];
  }, [template?.fields]);

  const scCategoryAssignmentField = useMemo(() => {
    return template?.fields.find((field) => field.key === SC_CATEGORY_ASSIGNMENT_KEY);
  }, [template?.fields]);

  const wallLossClassField = useMemo(() => {
    return template?.fields.find((field) => field.key === WALL_LOSS_CLASS_KEY);
  }, [template?.fields]);

  const wallLossClassAssignmentField = useMemo(() => {
    return template?.fields.find((field) => field.key === wallLossClassField?.detailFieldKey);
  }, [template?.fields, wallLossClassField?.detailFieldKey]);

  const alarmLevelField = useMemo(() => {
    return template?.fields.find((field) => field.key === 'alarm_level');
  }, [template?.fields]);

  const alarmLevelOptionsData = useMemo(() => {
    const optionsData =
      template?.fields.find((field) => field.key === 'alarm_level')?.optionsData ?? [];
    return optionsData;
  }, [template?.fields]);

  const alarmLevelAssignmentField = useMemo(() => {
    if (!alarmLevelField?.detailFieldKey) return null;
    return template?.fields.find((field) => field.key === alarmLevelField.detailFieldKey);
  }, [template?.fields, alarmLevelField?.detailFieldKey]);

  const { handleChange: handleFieldChange } = dataHook;

  useEffect(() => {
    setIsEvaluatingFields(isEvaluatingFields);
  }, [isEvaluatingFields, setIsEvaluatingFields]);

  const getScCategory = useCallback(
    (value: number) => {
      for (const category of scCategoryOptionsData) {
        const { min, max, minIncluded, maxIncluded } = category;
        const minCheck =
          min === null || min === undefined ? true : minIncluded ? value >= min : value > min;
        const maxCheck =
          max === null || max === undefined ? true : maxIncluded ? value <= max : value < max;

        if (minCheck && maxCheck) {
          return category.value;
        }
      }

      return 'SC1';
    },
    [scCategoryOptionsData]
  );

  const getAlarmLevel = useCallback(
    (rcaPercentage: number | null | undefined): string | null => {
      if (rcaPercentage === null || rcaPercentage === undefined) {
        return null;
      }

      for (const option of alarmLevelOptionsData) {
        const { min, max, minIncluded, maxIncluded, value } = option;
        const minCheck =
          min === null || min === undefined
            ? true
            : minIncluded
              ? rcaPercentage >= min
              : rcaPercentage > min;
        const maxCheck =
          max === null || max === undefined
            ? true
            : maxIncluded
              ? rcaPercentage <= max
              : rcaPercentage < max;

        if (minCheck && maxCheck) {
          return value;
        }
      }
      return null;
    },
    [alarmLevelOptionsData]
  );

  const [getAssemblySpecs, { data: assemblyPipeData }] = useGetAssemblyPipeSpecByIdLazyQuery({
    variables: {
      assemblyId: selectedAssemblyId,
    },
  });

  const { nominalCorrosionAllowance, nominalThickness } = useMemo(() => {
    return {
      nominalCorrosionAllowance:
        assemblyPipeData?.assembly?.pipeSpec?.nominalCorrosionAllowance ?? 0,
      nominalThickness: assemblyPipeData?.assembly?.pipeSpec?.nominalThickness ?? 0,
    };
  }, [assemblyPipeData]);

  useEffect(() => {
    if (pointOfInterest?.blister) {
      getAssemblySpecs();

      setLocalBlisterData({
        ...defaultBlisterData,
        ...pointOfInterest.blister,
      });

      const { estimatedHeight, manualSetHeight, scCategory, blisterHeightIndex } =
        pointOfInterest?.blister || {};

      setBlisterFieldsData((previous) => ({
        ...previous,
        ...(estimatedHeight ? { estimatedHeight: String(estimatedHeight) } : {}),
        ...(manualSetHeight ? { manualSetHeight: String(manualSetHeight) } : {}),
        ...(scCategoryMapping ? { scCategory: scCategory ?? 'SC1' } : {}),
        ...(blisterHeightIndex ? { blisterHeightIndex } : {}),
      }));
    }
  }, [getAssemblySpecs, pointOfInterest?.blister, scCategoryMapping]);

  const onMeasure = useCallback(
    (blisterData: MeasureBlisterQuery | undefined) => {
      if (!blisterData) return;
      const { blister, status } = blisterData?.fetchBlisterHeightMeasurement || {};

      const fittingRadius = status?.fittingRadius || 0;
      const estimatedHeight = blister?.maxHeight || 0;
      const borders3d = (blister?.borders3d ?? []).map((border3d) => ({
        points: border3d.points.map(({ x, y, z }) => ({ x, y, z })),
      }));

      setEditPointOfInterest((current) => {
        const { pointOfInterest: poi } = current;
        const centerPoint3d = blister?.centerPoint3d;
        if (!poi || !centerPoint3d) return current;
        const { x, y, z } = centerPoint3d;
        return {
          ...current,
          pointOfInterest: {
            ...poi,
            centerPoint3d: { x, y, z },
          },
          hasBlisterMeasurement: true,
        };
      });

      const governingValues = getGoverningValueCalculations(
        type,
        estimatedHeight,
        nominalCorrosionAllowance,
        nominalThickness,
        wallLossFactor
      );

      const isManualHeightPresent = blisterFieldsData?.manualSetHeight;
      const normalizedGoverningValues = normalizeGoverningValues(governingValues);

      // Calculate confidence fields
      let distanceConfidence: string | undefined;
      let measurementConfidence: string | undefined;

      const centerPoint3d = blister?.centerPoint3d;
      if (centerPoint3d && selectedSpherical?.position) {
        const { x, y, z } = centerPoint3d;
        const centerPoint3dArray = [x, y, z] as [number, number, number];
        const { meterDistance } = getDistanceFromPoint(
          selectedSpherical.position,
          centerPoint3dArray
        );

        if (meterDistance !== undefined) {
          distanceConfidence = getDistanceConfidence(meterDistance).value;

          // Get numeric pipe size for measurement confidence (angle not on measurement result)
          const pipeSpecSize = pointOfInterest?.assembly?.pipeSpec?.size;
          const pipeSizeNum =
            pipeSpecSize !== null
              ? (getPipeSize({
                  size: Number(pipeSpecSize),
                  unitSystem,
                  structureSteelPipes: structurePipesByStructureId?.[structureId],
                  displayUnit: false,
                }) as number)
              : undefined;

          measurementConfidence = calculateConfidence(meterDistance, pipeSizeNum, undefined);
        }
      }

      setLocalBlisterData((previous) => {
        const updatedBlisterData = {
          ...defaultBlisterData,
          ...previous,
          ...(isManualHeightPresent
            ? {}
            : { ...normalizedGoverningValues, maxHeight: estimatedHeight }),
          estimatedHeight,
          fittingRadius,
          borders3d,
          // Set calculated confidence fields
          ...(distanceConfidence && { distanceConfidence }),
          ...(measurementConfidence && { measurementConfidence }),
        };

        return updatedBlisterData;
      });

      setBlisterFieldsData((current) => ({
        ...current,
        estimatedHeight: String(estimatedHeight),
      }));
      if (borders3d.length > 0) {
        const newBlisterPolygon: PolygonProps = {
          id: pointOfInterest?.id,
          borderPoints: borders3d[0].points,
          isVisible: true,
          onClicked: () => {},
          name: 'newBlister',
        };
        setBlisterPolys((previous) => {
          if (!previous) return undefined;
          if (previous.some((polygon) => polygon?.id === pointOfInterest?.id)) return previous;
          return [...previous, newBlisterPolygon];
        });
      }
    },
    [
      setEditPointOfInterest,
      type,
      nominalCorrosionAllowance,
      nominalThickness,
      wallLossFactor,
      blisterFieldsData?.manualSetHeight,
      selectedSpherical?.position,
      getDistanceFromPoint,
      pointOfInterest?.assembly?.pipeSpec?.size,
      pointOfInterest?.id,
      unitSystem,
      structurePipesByStructureId,
      structureId,
      setBlisterPolys,
    ]
  );

  const updatePoiBlister = useCallback(
    (
      blisterHeight: number,
      shouldConvertHeight: boolean,
      blisterHeightIndex: InputMaybe<number> | undefined
    ) => {
      const convertHeight = (height: number) => {
        return unitSystem === UnitSystemEnum.Imperial
          ? height / METERS_TO_INCHES_FACTOR
          : height * MILLIMETERS_TO_METERS_FACTOR;
      };

      const convertedBlisterHeight = shouldConvertHeight
        ? convertHeight(blisterHeight)
        : blisterHeight;

      const governingValues = getGoverningValueCalculations(
        type,
        convertedBlisterHeight,
        nominalCorrosionAllowance,
        nominalThickness,
        wallLossFactor
      );
      const normalizedGoverningValues = normalizeGoverningValues(governingValues);

      setLocalBlisterData((previous) => ({
        ...defaultBlisterData,
        ...previous,
        ...normalizedGoverningValues,
        manualSetHeight: convertedBlisterHeight,
        maxHeight: convertedBlisterHeight,
        blisterHeightIndex,
        // Preserve confidence fields if they exist in previous state
        ...(previous?.distanceConfidence && { distanceConfidence: previous.distanceConfidence }),
        ...(previous?.measurementConfidence && {
          measurementConfidence: previous.measurementConfidence,
        }),
      }));
    },
    [nominalCorrosionAllowance, nominalThickness, type, unitSystem, wallLossFactor]
  );
  const pointSelectActiveRef = useRef(pointSelectMessage);

  const onSelectPoint = useCallback(
    (point: PointWithHeight, blisterHeightIndex: number) => {
      if (!pointSelectActiveRef.current) return;
      setBlisterFieldsData((current) => ({
        ...current,
        manualSetHeight: String(point.height),
        blisterHeightIndex,
      }));
      updatePoiBlister(point.height, false, blisterHeightIndex);
      setPointSelectMessage(false);
    },
    [updatePoiBlister]
  );

  const clearBlisterData = useCallback(() => {
    setLocalBlisterData((previous) => {
      const radiusPoint = previous?.radiusPoint || defaultBlisterData.radiusPoint;
      const centerPoint = previous?.centerPoint || defaultBlisterData.centerPoint;
      return {
        ...defaultBlisterData,
        radiusPoint,
        centerPoint,
        ...(scCategoryMapping ? { scCategory: 'SC1' } : {}),
        wallLoss: null,
        residualCorrosionAllowance: null,
        residualThickness: null,
      };
    });
    setBlisterFieldsData(undefined);
  }, [scCategoryMapping]);

  const handleMeasureIconClick = useCallback(() => {
    pointSelectActiveRef.current = true;
    setPointSelectMessage(true);
  }, []);
  const handleManualHeightChange = useCallback(
    (height: string) => {
      if (!height) {
        if (localBlisterData?.estimatedHeight) {
          const { estimatedHeight } = localBlisterData;
          const governingValues = getGoverningValueCalculations(
            type,
            estimatedHeight,
            nominalCorrosionAllowance,
            nominalThickness,
            wallLossFactor
          );
          const normalizedGoverningValues = normalizeGoverningValues(governingValues);

          setLocalBlisterData((previous) => ({
            ...defaultBlisterData,
            ...previous,
            ...normalizedGoverningValues,
            maxHeight: estimatedHeight,
            manualSetHeight: 0,
            // Preserve confidence fields if they exist in previous state
            ...(previous?.distanceConfidence && {
              distanceConfidence: previous.distanceConfidence,
            }),
            ...(previous?.measurementConfidence && {
              measurementConfidence: previous.measurementConfidence,
            }),
          }));
        } else {
          clearBlisterData();
        }
      }
    },
    [
      clearBlisterData,
      localBlisterData,
      nominalCorrosionAllowance,
      nominalThickness,
      type,
      wallLossFactor,
    ]
  );

  const handleChangeControlledField = useCallback(
    (key: string, value: string) => {
      if (key) {
        handleFieldChange(key, value);
      }

      const controlledField = template?.fields.find(
        (field) => field.key === key && !!field.detailFieldKey
      );
      if (!controlledField) return;
      const assignmentField = template?.fields.find(
        (field) => field.key === controlledField.detailFieldKey
      );

      if (assignmentField && assignmentField.key) {
        handleFieldChange(assignmentField.key, ControlledFieldAssignment.MANUALLY_ADDED);
      }
    },
    [handleFieldChange, template?.fields]
  );

  const handleChangeScCategory = useCallback(
    (scCategory: string) => {
      setBlisterFieldsData((current) => ({
        ...current,
        ...(scCategoryMapping ? { scCategory: scCategory ?? 'SC1' } : ''),
      }));
      setLocalBlisterData((current) => ({
        ...defaultBlisterData,
        ...current,
        ...(scCategoryMapping ? { scCategory: scCategory ?? 'SC1' } : ''),
        // Preserve confidence fields if they exist in current state
        ...(current?.distanceConfidence && { distanceConfidence: current.distanceConfidence }),
        ...(current?.measurementConfidence && {
          measurementConfidence: current.measurementConfidence,
        }),
      }));
    },
    [scCategoryMapping]
  );

  const blisterMaxHeight = useMemo(() => {
    return localBlisterData?.maxHeight;
  }, [localBlisterData?.maxHeight]);

  const blisterRCA = useMemo(() => {
    return localBlisterData?.residualCorrosionAllowance?.percentage;
  }, [localBlisterData?.residualCorrosionAllowance?.percentage]);

  useEffect(() => {
    if (scCategoryField) {
      const scCategory = getScCategory(blisterMaxHeight ?? 0);
      handleFieldChange(scCategoryField.key ?? '', scCategory);

      if (scCategoryAssignmentField) {
        handleFieldChange(scCategoryAssignmentField.key ?? '', ScCategoryAssignment.AUTO_GENERATED);
      }
    }
  }, [
    blisterMaxHeight,
    editPointOfInterest.formState,
    getScCategory,
    handleFieldChange,
    scCategoryAssignmentField,
    scCategoryField,
    type,
  ]);

  useEffect(() => {
    if (
      blisterRCA !== null &&
      blisterRCA !== undefined &&
      alarmLevelField &&
      alarmLevelOptionsData.length > 0
    ) {
      const calculatedAlarmLevel = getAlarmLevel(blisterRCA);
      if (calculatedAlarmLevel && alarmLevelField.key) {
        handleFieldChange(alarmLevelField.key, calculatedAlarmLevel);
        if (alarmLevelAssignmentField?.key) {
          handleFieldChange(
            alarmLevelAssignmentField.key,
            ControlledFieldAssignment.AUTO_GENERATED
          );
        }
      }
    }
  }, [
    blisterRCA,
    editPointOfInterest.formState,
    getAlarmLevel,
    handleFieldChange,
    alarmLevelAssignmentField,
    alarmLevelField,
    alarmLevelOptionsData.length,
    type,
  ]);

  const handleWallLossEvaluation = useCallback(async () => {
    if (blisterRCA && wallLossClassField) {
      const evaluation = await evaluateFieldLogic({
        variables: {
          input: {
            templateType: TemplateTypeEnum.PointOfInterest,
            fieldKey: wallLossClassField.key ?? '',
            structureId,
            templateId: template?.id ?? '',
            context: {
              RCA: blisterRCA.toString(),
              blisterHeight: (blisterMaxHeight ?? 0)?.toString(),
            },
          },
        },
      });

      if (evaluation.data?.evaluateTemplateFieldLogicRule?.result) {
        handleFieldChange(
          wallLossClassField.id,
          evaluation.data.evaluateTemplateFieldLogicRule.result
        );
        if (wallLossClassAssignmentField) {
          handleFieldChange(
            wallLossClassAssignmentField.id,
            ControlledFieldAssignment.AUTO_GENERATED
          );
        }
      }
    }
  }, [
    blisterMaxHeight,
    blisterRCA,
    evaluateFieldLogic,
    handleFieldChange,
    structureId,
    template?.id,
    wallLossClassAssignmentField,
    wallLossClassField,
  ]);

  useEffect(() => {
    if (blisterRCA && wallLossClassField && blisterMaxHeight) {
      handleWallLossEvaluation();
    }
  }, [blisterRCA, wallLossClassField, handleWallLossEvaluation, blisterMaxHeight]);

  useEffect(() => {
    if (localBlisterData?.manualSetHeight) {
      const manualHeight = localBlisterData?.manualSetHeight;
      const governingValues = getGoverningValueCalculations(
        type,
        manualHeight,
        nominalCorrosionAllowance,
        nominalThickness,
        wallLossFactor
      );
      const normalizedGoverningValues = normalizeGoverningValues(governingValues);

      setLocalBlisterData((previous) => ({
        ...defaultBlisterData,
        ...previous,
        ...normalizedGoverningValues,
        manualSetHeight: manualHeight,
        maxHeight: manualHeight,
        // Preserve confidence fields if they exist in previous state
        ...(previous?.distanceConfidence && { distanceConfidence: previous.distanceConfidence }),
        ...(previous?.measurementConfidence && {
          measurementConfidence: previous.measurementConfidence,
        }),
      }));
    }
  }, [
    nominalCorrosionAllowance,
    nominalThickness,
    type,
    wallLossFactor,
    localBlisterData?.manualSetHeight,
  ]);

  return {
    onMeasure,
    pointSelectMessage,
    handleManualHeightChange,
    onSelectPoint,
    clearBlisterData,
    handleMeasureIconClick,
    handleChangeScCategory,
    localBlisterData,
    blisterFieldsData,
    updatePoiBlister,
    setLocalBlisterData,
    handleChangeControlledField,
  };
};
